import React, { useState, useEffect, useRef } from "react";
import { API, Auth } from "aws-amplify";
import { useLocation } from "react-router-dom";
import ReactPlayer from "react-player";
import { listCandidateProfiles } from "../../graphql/queries";
import Header from "../../components/header/Header";
import profilephoto from "../../assets/profile.png";
import { RiseLoader } from "react-spinners";
import { getWebMPlayerConfig } from "../../utils/webmSeekingUtils";
import { buildResumeURL } from "../../utils/resumeUrl";
import { 
  FiShare2, 
  FiCopy, 
  FiX, 
  FiUser, 
  FiMail, 
  FiBriefcase,
  FiVideo,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare
} from "react-icons/fi";
import { FaWhatsapp, FaLinkedin, FaFacebook, FaInstagram } from "react-icons/fa";
import { BiLogoGmail } from "react-icons/bi";

const AdminCandidateViewProfile = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [slotId, setSlotId] = useState("");
  const [fullName, setFullName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [workStatus, setWorkStatus] = useState("");
  const [description, setDescription] = useState("");
  const [keySkills, setKeySkills] = useState("");
  const [resumeURL, setResumeURL] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [interviewEvaluations, setInterviewEvaluations] = useState([]);
  const [interviewSlot, setInterviewSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setEmail(searchParams.get("email") || "");
    setSlotId(searchParams.get("slotId") || "");
    setFullName(searchParams.get("fullName") || "");
    setPhotoURL(searchParams.get("photoURL") || "");
    setWorkStatus(searchParams.get("workStatus") || "");
    setDescription(searchParams.get("description") || "");
    setKeySkills(searchParams.get("keySkills") || "");
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      if (!email && !slotId) return;

      setLoading(true);
      setError("");
      
      let queryEmail = email; // Will be updated if slot provides email

      try {
        await Auth.currentAuthenticatedUser();

        // If slotId is provided, fetch the specific interview slot with its evaluation
        if (slotId) {
          let slotResponse;
          try {
            slotResponse = await API.graphql({
              query: `
                query GetInterviewSlot($id: ID!) {
                  getInterviewSlot(id: $id) {
                    id
                    candidateEmail
                    candidateProfileID
                    date
                    startTime
                    endTime
                    status
                    meetingLinkURL
                    candidateAttendance
                    botAttendance
                    jdID
                    createdAt
                    interviewEvaluation {
                      id
                      candidateEmail
                      roundNumber
                      evaluation
                      transcript
                      videoURL
                      feedbackForAI
                      createdAt
                      interviewSlotID
                    }
                    candidateProfile {
                      id
                      fullName
                      email
                      mobileNumber
                      photoURL
                      roleApplyFor
                      workStatus
                      description
                      keySkills
                      resumeURL
                    }
                  }
                }
              `,
              variables: { id: slotId }
            });
          } catch (partialError) {
            if (partialError.data?.getInterviewSlot) {
              slotResponse = partialError;
            } else {
              console.error("Slot fetch error:", partialError);
            }
          }

          if (slotResponse?.data?.getInterviewSlot) {
            const slot = slotResponse.data.getInterviewSlot;
            setInterviewSlot(slot);
            
            // Use email from slot if not provided in URL
            if (slot.candidateEmail) {
              queryEmail = slot.candidateEmail;
              if (!email) setEmail(slot.candidateEmail);
            }
            
            // Set candidate info from slot's candidateProfile if available
            if (slot.candidateProfile) {
              setFullName(slot.candidateProfile.fullName || fullName || "");
              setPhotoURL(slot.candidateProfile.photoURL || photoURL || "");
              setWorkStatus(slot.candidateProfile.workStatus || workStatus || "");
              setDescription(slot.candidateProfile.description || description || "");
              setKeySkills(slot.candidateProfile.keySkills || keySkills || "");
              setResumeURL(slot.candidateProfile.resumeURL || resumeURL || "");
            }
          }
        }
        
        if (!queryEmail) {
          setError("No email found. Please provide an email or valid slot ID.");
          setLoading(false);
          return;
        }

        // Fetch admin contexts
        let adminContextResponse;
        try {
          adminContextResponse = await API.graphql({
            query: `
              query ListAdminAddCandidateContexts($filter: ModelAdminAddCandidateContextFilterInput, $limit: Int) {
                listAdminAddCandidateContexts(filter: $filter, limit: $limit) {
                  items {
                    id
                    candidateEmail
                    candidateVideo
                    candidateContextTextFile
                    createdAt
                  }
                }
              }
            `,
            variables: {
              filter: { candidateEmail: { eq: queryEmail } },
              limit: 100,
            }
          });
        } catch (partialError) {
          if (partialError.data?.listAdminAddCandidateContexts) {
            adminContextResponse = partialError;
          }
        }

        if (adminContextResponse?.data?.listAdminAddCandidateContexts?.items) {
          const items = adminContextResponse.data.listAdminAddCandidateContexts.items;
          setProfiles(items.filter(item => item && item.id));
        }

        // Fetch interview evaluations - if slotId is provided, ONLY fetch for that slot
        let evaluationsFromEmail = [];
        let evaluationsResponse;
        let nextToken = null;
        
        // Determine filter based on whether slotId is provided
        const evaluationFilter = slotId 
          ? { interviewSlotID: { eq: slotId } }
          : { candidateEmail: { eq: queryEmail } };
        
        try {
          do {
            evaluationsResponse = await API.graphql({
              query: `
                query ListInterviewEvaluations($filter: ModelInterviewEvaluationFilterInput, $limit: Int, $nextToken: String) {
                  listInterviewEvaluations(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                      id
                      candidateEmail
                      roundNumber
                      evaluation
                      transcript
                      videoURL
                      feedbackForAI
                      createdAt
                      updatedAt
                      interviewSlotID
                      reappearRoundNumber
                    }
                    nextToken
                  }
                }
              `,
              variables: {
                filter: evaluationFilter,
                limit: 100,
                nextToken: nextToken
              }
            });
            
            if (evaluationsResponse?.data?.listInterviewEvaluations?.items) {
              const items = evaluationsResponse.data.listInterviewEvaluations.items.filter(item => item && item.id);
              evaluationsFromEmail.push(...items);
              nextToken = evaluationsResponse.data.listInterviewEvaluations.nextToken;
            } else {
              break;
            }
          } while (nextToken);
        } catch (partialError) {
          if (partialError.data?.listInterviewEvaluations?.items) {
            const items = partialError.data.listInterviewEvaluations.items.filter(item => item && item.id);
            evaluationsFromEmail = items;
          }
        }
        
        // Set evaluations (sorted by createdAt descending)
        if (evaluationsFromEmail.length > 0) {
          evaluationsFromEmail.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setInterviewEvaluations(evaluationsFromEmail);
        }

        // Always fetch candidate profile to get complete data (including resume)
        if (queryEmail) {
          console.log("Fetching candidate profile for email:", queryEmail);
          let profileFound = null;
          let nextToken = null;
          
          try {
            // Paginate through all profiles to find the matching email
            do {
              const profileResponse = await API.graphql({
                query: listCandidateProfiles,
                variables: {
                  filter: { email: { eq: queryEmail } },
                  limit: 1000,
                  nextToken: nextToken
                }
              });
              
              console.log("Profile response (page):", profileResponse);
              
              if (profileResponse?.data?.listCandidateProfiles?.items) {
                const items = profileResponse.data.listCandidateProfiles.items.filter(i => i);
                console.log("Profile items found in this page:", items.length);
                
                if (items.length > 0) {
                  profileFound = items[0];
                  break; // Found the profile, exit loop
                }
                
                nextToken = profileResponse.data.listCandidateProfiles.nextToken;
              } else {
                break;
              }
            } while (nextToken);
          } catch (err) {
            console.error("Error fetching profile:", err);
          }

          if (profileFound) {
            const items = [profileFound];
            console.log("Profile items found:", items.length);
            if (items.length > 0) {
              const profile = items[0];
              console.log("\n=== Candidate Profile Details ===");
              console.log("Full Name:", profile.fullName || "N/A");
              console.log("Email:", profile.email || "N/A");
              console.log("Mobile:", profile.mobileNumber || "N/A");
              console.log("Work Status:", profile.workStatus || "N/A");
              console.log("Role:", profile.roleApplyFor || "N/A");
              console.log("Photo URL:", profile.photoURL || "N/A");
              console.log("Resume URL:", profile.resumeURL || "N/A");
              console.log("Key Skills:", profile.keySkills || "N/A");
              console.log("Description:", profile.description || "N/A");
              console.log("================================\n");
              
              if (profile.fullName) setFullName(profile.fullName);
              if (profile.photoURL) setPhotoURL(profile.photoURL);
              if (profile.workStatus) setWorkStatus(profile.workStatus);
              if (profile.description) setDescription(profile.description);
              if (profile.keySkills) setKeySkills(profile.keySkills);
              if (profile.resumeURL) setResumeURL(profile.resumeURL);
            } else {
              console.log("No profile items found for email:", queryEmail);
            }
          } else {
            console.log("No profile found after pagination for email:", queryEmail);
          }
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        // Check if partial data is available in the error
        if (err.data) {
          if (err.data.listAdminAddCandidateContexts?.items) {
            const items = err.data.listAdminAddCandidateContexts.items;
            setProfiles(items.filter(item => item && item.id));
          }
          if (err.data.listInterviewEvaluations?.items) {
            const items = err.data.listInterviewEvaluations.items;
            setInterviewEvaluations(items.filter(item => item && item.id));
          }
          if (err.data.getInterviewSlot) {
            setInterviewSlot(err.data.getInterviewSlot);
          }
        } else {
          setError("Failed to load candidate data. Please check your permissions.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [email, slotId]);

  const handleShareVia = (platform) => {
    const encodedEmail = encodeURIComponent(email);
    const shareableURL = `${window.location.origin}/ShowMySkills?email=${encodedEmail}`;
    let shareURL = "";

    switch (platform) {
      case "whatsapp":
        shareURL = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareableURL)}`;
        break;
      case "linkedin":
        shareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableURL)}`;
        break;
      case "facebook":
        shareURL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareableURL)}`;
        break;
      case "instagram":
        shareURL = `https://www.instagram.com/share?url=${encodeURIComponent(shareableURL)}`;
        break;
      case "email":
        const subject = encodeURIComponent("Check out this profile");
        const body = encodeURIComponent(`Hi, I thought you might be interested in this profile: ${shareableURL}`);
        shareURL = `mailto:?subject=${subject}&body=${body}`;
        break;
      default:
        break;
    }

    window.open(shareURL, '_blank');
    setShowModal(false);
  };

  const handleCopyURL = () => {
    const shareableURL = `${window.location.origin}/ShowMySkills?email=${encodeURIComponent(email)}`;
    navigator.clipboard.writeText(shareableURL);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Share Modal Component
  const ShareModal = () => {
    const modalRef = useRef();

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          setShowModal(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div ref={modalRef} className="bg-slate-800 rounded-xl p-6 border border-slate-700 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-semibold">Share Profile</h3>
            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
              <FiX size={20} />
            </button>
          </div>
          <div className="flex justify-center gap-4 mb-4">
            <button onClick={handleCopyURL} className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
              <FiCopy size={24} className="text-blue-400" />
            </button>
            <button onClick={() => handleShareVia("whatsapp")} className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
              <FaWhatsapp size={24} className="text-green-400" />
            </button>
            <button onClick={() => handleShareVia("linkedin")} className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
              <FaLinkedin size={24} className="text-blue-500" />
            </button>
            <button onClick={() => handleShareVia("facebook")} className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
              <FaFacebook size={24} className="text-blue-600" />
            </button>
            <button onClick={() => handleShareVia("email")} className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
              <BiLogoGmail size={24} className="text-red-400" />
            </button>
          </div>
          {copied && (
            <p className="text-green-400 text-center text-sm flex items-center justify-center gap-2">
              <FiCheckCircle /> Link copied to clipboard!
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 rounded-lg p-4 mb-6 flex items-center gap-2">
            <FiXCircle size={20} />
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <RiseLoader color={"#3b82f6"} loading={loading} css={override} size={15} />
          </div>
        )}

        {/* Profile Header Card */}
        <div className="bg-slate-800/40 rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <img 
                src={photoURL || profilephoto} 
                alt={fullName || "Candidate"} 
                className="w-32 h-32 rounded-full object-cover border-4 border-slate-700"
              />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-semibold text-white mb-1">
                    {fullName || "Unknown Candidate"}
                  </h1>
                  <div className="flex items-center gap-2 text-slate-400 mb-2">
                    <FiBriefcase size={16} />
                    <span>{workStatus || "Not specified"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <FiMail size={16} />
                    <span>{email || "No email"}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`/CompanyCandidateViewProfilev1/${encodeURIComponent(email)}${slotId ? `?slotId=${slotId}` : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-medium"
                  >
                    <FiUser size={16} />
                    View Full Profile
                  </a>
                  <button 
                    onClick={() => setShowModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-medium"
                  >
                    <FiShare2 size={16} />
                    Share Profile
                  </button>
                </div>
              </div>
              
              {description && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-slate-300 text-sm leading-relaxed">{description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Skills */}
          {keySkills && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <h3 className="text-slate-400 text-sm uppercase mb-3">Key Skills</h3>
              <div className="flex flex-wrap gap-2">
                {keySkills.split(",").map((skill, idx) => (
                  <span key={idx} className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm">
                    {skill.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Resume */}
          {buildResumeURL(resumeURL) && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <h3 className="text-slate-400 text-sm uppercase mb-3">Resume</h3>
              <a
                href={buildResumeURL(resumeURL)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium"
              >
                <FiFileText size={16} />
                View Resume
              </a>
            </div>
          )}
        </div>

        {/* Interview Slot Info (if slotId provided) */}
        {interviewSlot && (() => {
          // Effective attendance: raw flag, OR any evaluation exists for this slot with real
          // content (transcript/video/evaluation). The backend boolean often isn't flipped
          // even after a successful interview, but the presence of an evaluation is authoritative.
          const slotEvaluations = interviewEvaluations.filter(
            (ev) => ev && ev.interviewSlotID === interviewSlot.id
          );
          const hasEvaluationContent = slotEvaluations.some(
            (ev) => ev.transcript || ev.videoURL || ev.evaluation
          );
          const isCompleted = interviewSlot.status?.toLowerCase() === 'completed';
          const effectiveCandidatePresent =
            !!interviewSlot.candidateAttendance || hasEvaluationContent;
          const effectiveBotActive =
            !!interviewSlot.botAttendance || hasEvaluationContent || isCompleted;

          return (
          <div className="bg-slate-800/40 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FiVideo className="text-purple-400" />
              Interview Slot Details
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Date</p>
                <p className="text-white font-medium">{interviewSlot.date || 'N/A'}</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Time</p>
                <p className="text-white font-medium">
                  {interviewSlot.startTime && interviewSlot.endTime
                    ? `${interviewSlot.startTime} - ${interviewSlot.endTime}`
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Status</p>
                <span className={`px-2 py-0.5 rounded text-sm ${
                  isCompleted
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {interviewSlot.status || 'pending'}
                </span>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Candidate Attendance</p>
                <p className={`font-medium ${effectiveCandidatePresent ? 'text-green-400' : 'text-slate-400'}`}>
                  {effectiveCandidatePresent ? 'Present' : 'Not Recorded'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Bot Attendance</p>
                <p className={`font-medium ${effectiveBotActive ? 'text-green-400' : 'text-slate-400'}`}>
                  {effectiveBotActive ? 'Active' : 'Not Recorded'}
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Slot ID</p>
                <p className="text-white font-mono text-xs truncate" title={interviewSlot.id}>
                  {interviewSlot.id?.slice(0, 12)}...
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">JD ID</p>
                <p className="text-white font-mono text-xs truncate" title={interviewSlot.jdID}>
                  {interviewSlot.jdID ? `${interviewSlot.jdID.slice(0, 12)}...` : 'N/A'}
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase mb-1">Created</p>
                <p className="text-white text-sm">
                  {interviewSlot.createdAt ? new Date(interviewSlot.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            {interviewSlot.meetingLinkURL && (
              <div className="mt-4 p-3 bg-slate-700/30 rounded-lg">
                <p className="text-slate-400 text-xs uppercase mb-1">Meeting Link</p>
                <a
                  href={interviewSlot.meetingLinkURL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-sm underline break-all"
                >
                  {interviewSlot.meetingLinkURL}
                </a>
              </div>
            )}
          </div>
          );
        })()}

        {/* Interview Evaluations */}
        {interviewEvaluations.length > 0 && (
          <div className="bg-slate-800/40 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FiCheckCircle className="text-green-400" />
              Interview Evaluations ({interviewEvaluations.length})
            </h2>
            
            <div className="space-y-6">
              {interviewEvaluations.map((evaluation, index) => {
                let parsedEvaluation = null;
                let totalScore = 0;
                let totalMaxScore = 0;
                let overallRating = "N/A";

                try {
                  if (evaluation.evaluation) {
                    parsedEvaluation = JSON.parse(evaluation.evaluation);
                    
                    // Check if it's the expected format (each key has score/max_score)
                    const firstKey = Object.keys(parsedEvaluation)[0];
                    if (parsedEvaluation[firstKey] && typeof parsedEvaluation[firstKey] === 'object' && 'score' in parsedEvaluation[firstKey]) {
                      totalScore = Object.values(parsedEvaluation).reduce((sum, data) => sum + (data.score || 0), 0);
                      totalMaxScore = Object.values(parsedEvaluation).reduce((sum, data) => sum + (data.max_score || 0), 0);
                      // Calculate percentage out of 100
                      overallRating = totalMaxScore ? Math.round((totalScore / totalMaxScore) * 100) : "N/A";
                    }
                  }
                } catch (e) {
                  console.error("Error parsing evaluation:", e);
                }

                let parsedTranscript = null;
                let transcriptArray = [];
                let rawTranscriptText = null;
                
                try {
                  if (evaluation.transcript) {
                    parsedTranscript = JSON.parse(evaluation.transcript);
                    
                    // Handle different transcript formats
                    if (Array.isArray(parsedTranscript)) {
                      // Map to unified format - handle both old (speaker/message) and new (type/content) formats
                      transcriptArray = parsedTranscript
                        .filter(item => item && (item.content || item.message))
                        .map(item => {
                          // New format: type + content
                          if (item.type) {
                            let speaker = "";
                            let isAI = false;
                            let isTermination = false;
                            
                            switch (item.type) {
                              case "ai_question":
                                speaker = "AI Interviewer";
                                isAI = true;
                                break;
                              case "user_response":
                                speaker = "Candidate";
                                break;
                              case "interview_termination":
                                speaker = "System";
                                isTermination = true;
                                break;
                              default:
                                speaker = item.type;
                            }
                            
                            return {
                              speaker,
                              message: item.content || item.message || "",
                              timestamp: item.timestamp,
                              questionNumber: item.question_number,
                              detectedSkills: item.detected_skills,
                              isAI,
                              isTermination,
                              type: item.type
                            };
                          }
                          // Old format: speaker + message
                          else if (item.speaker && item.message) {
                            return {
                              speaker: item.speaker,
                              message: item.message,
                              isAI: item.speaker.toLowerCase().includes('ai') || item.speaker.toLowerCase().includes('interviewer'),
                              isTermination: false
                            };
                          }
                          return null;
                        })
                        .filter(item => item && item.message && item.message.trim() !== '');
                    } else if (typeof parsedTranscript === 'object') {
                      // If it's an object, try to convert to array format
                      transcriptArray = Object.entries(parsedTranscript)
                        .filter(([key, value]) => key && value && key.trim() !== '' && value.toString().trim() !== '')
                        .map(([key, value]) => ({
                          speaker: key,
                          message: typeof value === 'string' ? value : JSON.stringify(value),
                          isAI: key.toLowerCase().includes('ai') || key.toLowerCase().includes('interviewer'),
                          isTermination: false
                        }));
                    }
                    
                    // If no valid entries after filtering, store raw for display
                    if (transcriptArray.length === 0) {
                      rawTranscriptText = typeof parsedTranscript === 'string' 
                        ? parsedTranscript 
                        : JSON.stringify(parsedTranscript, null, 2);
                    }
                  }
                } catch (e) {
                  console.error("Error parsing transcript:", e);
                  // If JSON parsing fails, store raw text for display
                  if (evaluation.transcript && evaluation.transcript.trim() !== '') {
                    rawTranscriptText = evaluation.transcript;
                  }
                }

                return (
                  <div key={evaluation.id || index} className="bg-slate-700/40 rounded-lg p-5 border border-slate-600">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <span className="text-blue-400 font-semibold text-lg">
                          Round {evaluation.roundNumber || index + 1}
                        </span>
                        <p className="text-slate-400 text-xs mt-1">
                          {new Date(evaluation.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <a
                        href={`/CompanyCandidateViewProfilev1/${encodeURIComponent(email)}?slotId=${evaluation.interviewSlotID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-2 text-xs font-medium"
                      >
                        <FiUser size={14} />
                        View Evaluation
                      </a>
                    </div>

                    {/* Overall Score - Displayed First */}
                    {overallRating !== "N/A" && (
                      <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 border-2 border-green-500/50 rounded-lg p-4 mb-6">
                        <p className="text-slate-400 text-xs uppercase tracking-wide mb-2">Overall Score</p>
                        <p className="text-green-400 font-bold text-3xl">
                          {overallRating}<span className="text-xl">/100</span>
                        </p>
                      </div>
                    )}

                    {/* Video */}
                    <div className="mb-6">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <FiVideo className="text-blue-400" />
                        Interview Recording
                      </h4>
                      {evaluation.videoURL ? (
                        <div
                          className="w-full rounded-lg border border-slate-600 overflow-hidden bg-black"
                          style={{ aspectRatio: "16/9", maxHeight: "400px" }}
                        >
                          <ReactPlayer
                            url={evaluation.videoURL}
                            controls
                            width="100%"
                            height="100%"
                            config={getWebMPlayerConfig(evaluation.videoURL)}
                            onError={(e) => console.error("Video playback error:", e, evaluation.videoURL)}
                          />
                        </div>
                      ) : (
                        <div className="bg-slate-800/60 rounded-lg p-6 text-center border border-slate-600">
                          <FiXCircle className="text-slate-500 text-3xl mx-auto mb-2" />
                          <p className="text-slate-400 text-sm">No video recording available for this interview</p>
                        </div>
                      )}
                    </div>

                    {/* Transcript */}
                    {(transcriptArray.length > 0 || rawTranscriptText) && (
                      <div className="mb-6">
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <FiMessageSquare className="text-blue-400" />
                          Interview Transcript
                        </h4>
                        <div className="bg-slate-800/60 rounded-lg p-4 max-h-[500px] overflow-y-auto space-y-3">
                          {transcriptArray.length > 0 ? (
                            transcriptArray.map((item, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border-l-4 ${
                                  item.isTermination 
                                    ? 'bg-red-500/10 border-l-red-500' 
                                    : item.isAI 
                                      ? 'bg-blue-500/10 border-l-blue-500' 
                                      : 'bg-purple-500/10 border-l-purple-500'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`text-xs font-semibold uppercase tracking-wide ${
                                    item.isTermination 
                                      ? 'text-red-400' 
                                      : item.isAI 
                                        ? 'text-blue-400' 
                                        : 'text-purple-400'
                                  }`}>
                                    {item.speaker}
                                    {item.questionNumber && ` (Q${item.questionNumber})`}
                                  </span>
                                  {item.timestamp && (
                                    <span className="text-slate-500 text-[10px]">{item.timestamp}</span>
                                  )}
                                </div>
                                <p className="text-slate-200 text-sm leading-relaxed">{item.message}</p>
                                {item.detectedSkills && item.detectedSkills.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {item.detectedSkills.map((skill, skillIdx) => (
                                      <span 
                                        key={skillIdx} 
                                        className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full"
                                      >
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : rawTranscriptText ? (
                            <div className="text-slate-300 text-sm whitespace-pre-wrap">
                              {rawTranscriptText}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {/* Evaluation Scores */}
                    {parsedEvaluation && (
                      <div>
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <FiFileText className="text-blue-400" />
                          Evaluation Scores
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(parsedEvaluation).map(([criterion, data], idx) => {
                            // Special rendering functions for different data types
                            const criterionLower = criterion.toLowerCase();
                            
                            // Render technical skills as progress bars
                            const renderTechnicalSkills = (skills) => {
                              if (!skills || typeof skills !== 'object') return null;
                              return (
                                <div className="space-y-2 mt-2">
                                  {Object.entries(skills).map(([skill, score]) => (
                                    <div key={skill}>
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="text-slate-300">{skill}</span>
                                        <span className="text-blue-400 font-semibold">{score}/100</span>
                                      </div>
                                      <div className="w-full bg-slate-700 rounded-full h-1.5">
                                        <div 
                                          className={`h-1.5 rounded-full ${score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                          style={{ width: `${score}%` }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            };

                            // Render soft skills as badges
                            const renderSoftSkills = (skills) => {
                              if (!skills || typeof skills !== 'object') return null;
                              return (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {Object.entries(skills).map(([skill, score]) => (
                                    <div 
                                      key={skill}
                                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                        score >= 70 ? 'bg-green-500/20 text-green-400' : 
                                        score >= 40 ? 'bg-yellow-500/20 text-yellow-400' : 
                                        'bg-red-500/20 text-red-400'
                                      }`}
                                    >
                                      {skill}: {score}/100
                                    </div>
                                  ))}
                                </div>
                              );
                            };

                            // Render question analysis
                            const renderQuestionAnalysis = (analysis) => {
                              if (!analysis || typeof analysis !== 'object') return null;
                              return (
                                <div className="space-y-3 mt-2">
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div className="bg-slate-700/50 rounded p-2 text-center">
                                      <div className="text-slate-400 mb-1">Total</div>
                                      <div className="text-white font-bold">{analysis.total_questions || 0}</div>
                                    </div>
                                    <div className="bg-green-500/20 rounded p-2 text-center">
                                      <div className="text-slate-400 mb-1">Strong</div>
                                      <div className="text-green-400 font-bold">{analysis.strong_responses || 0}</div>
                                    </div>
                                    <div className="bg-red-500/20 rounded p-2 text-center">
                                      <div className="text-slate-400 mb-1">Weak</div>
                                      <div className="text-red-400 font-bold">{analysis.weak_responses || 0}</div>
                                    </div>
                                  </div>
                                  {analysis.question_breakdown && Array.isArray(analysis.question_breakdown) && (
                                    <div className="space-y-2">
                                      {analysis.question_breakdown.map((q, i) => (
                                        <div key={i} className="bg-slate-700/50 rounded p-2 text-xs">
                                          <div className="flex justify-between mb-1">
                                            <span className="text-slate-300 font-medium">{q.topic}</span>
                                            <span className={`px-2 py-0.5 rounded text-[10px] ${
                                              q.performance === 'Strong' ? 'bg-green-500/20 text-green-400' :
                                              q.performance === 'Weak' ? 'bg-red-500/20 text-red-400' :
                                              'bg-yellow-500/20 text-yellow-400'
                                            }`}>
                                              {q.performance}
                                            </span>
                                          </div>
                                          <p className="text-slate-400">{q.key_insights}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            };

                            // Render timestamp analysis
                            const renderTimestampAnalysis = (timestamps) => {
                              if (!timestamps) return null;
                              // Handle if it's a string of multiple JSON objects
                              let timestampArray = [];
                              if (typeof timestamps === 'string') {
                                const lines = timestamps.split('\n').filter(line => line.trim());
                                timestampArray = lines.map(line => {
                                  try { return JSON.parse(line); } catch { return null; }
                                }).filter(Boolean);
                              } else if (Array.isArray(timestamps)) {
                                timestampArray = timestamps;
                              }
                              
                              if (timestampArray.length === 0) return null;
                              
                              return (
                                <div className="space-y-2 mt-2">
                                  {timestampArray.map((t, i) => (
                                    <div key={i} className="bg-slate-700/50 rounded p-2 text-xs">
                                      <div className="flex justify-between mb-1">
                                        <span className="text-blue-400">Q{t.question_number}</span>
                                        <span className="text-slate-400">{t.start_time} - {t.end_time}</span>
                                      </div>
                                      <div className="text-slate-300 mb-1">{t.skill_tested}</div>
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                                          <div 
                                            className={`h-1.5 rounded-full ${
                                              t.performance_level === 'Strong' ? 'bg-green-500' :
                                              t.performance_level === 'Weak' ? 'bg-red-500' :
                                              t.performance_level === 'Critical' ? 'bg-red-600' :
                                              'bg-yellow-500'
                                            }`}
                                            style={{ width: `${t.performance_score}%` }}
                                          />
                                        </div>
                                        <span className="text-slate-400 text-[10px] w-12">{t.performance_score}/100</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            };

                            // Render score out of 100
                            const renderScore = (score) => {
                              const numScore = typeof score === 'number' ? score : parseInt(score);
                              if (isNaN(numScore)) return 'N/A';
                              return (
                                <div className="mt-2">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-slate-700 rounded-full h-2">
                                      <div 
                                        className={`h-2 rounded-full ${
                                          numScore >= 70 ? 'bg-green-500' :
                                          numScore >= 40 ? 'bg-yellow-500' :
                                          'bg-red-500'
                                        }`}
                                        style={{ width: `${numScore}%` }}
                                      />
                                    </div>
                                    <span className="text-blue-400 font-bold text-sm">{numScore}/100</span>
                                  </div>
                                </div>
                              );
                            };

                            // Determine how to render based on field name and data type
                            let content;
                            
                            if (criterionLower.includes('technical') && criterionLower.includes('skill')) {
                              content = renderTechnicalSkills(data);
                            } else if (criterionLower.includes('soft') && criterionLower.includes('skill')) {
                              content = renderSoftSkills(data);
                            } else if (criterionLower.includes('question') && criterionLower.includes('analysis')) {
                              content = renderQuestionAnalysis(data);
                            } else if (criterionLower.includes('timestamp') && criterionLower.includes('analysis')) {
                              content = renderTimestampAnalysis(data);
                            } else if (criterionLower.includes('score') && typeof data === 'number') {
                              content = renderScore(data);
                            } else if (typeof data === 'string' && data.length > 100) {
                              // Long text - show in text area
                              content = <p className="text-slate-400 text-xs mt-2 whitespace-pre-wrap">{data}</p>;
                            } else if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
                              content = <p className="text-slate-400 text-xs mt-2">{data.toString()}</p>;
                            } else if (typeof data === 'object' && data !== null) {
                              // Try to parse if it's a JSON string
                              let parsedData = data;
                              if (typeof data === 'string') {
                                try {
                                  parsedData = JSON.parse(data);
                                } catch {
                                  parsedData = data;
                                }
                              }
                              
                              // Display as formatted JSON
                              content = (
                                <pre className="text-xs mt-2 text-slate-400 whitespace-pre-wrap break-words bg-slate-700/50 rounded p-2 max-h-40 overflow-y-auto">
                                  {JSON.stringify(parsedData, null, 2)}
                                </pre>
                              );
                            } else {
                              content = <p className="text-slate-400 text-xs mt-2">N/A</p>;
                            }

                            return (
                              <div key={idx} className="bg-slate-800/60 rounded-lg p-3">
                                <span className="text-slate-300 text-sm font-medium capitalize block mb-1">
                                  {criterion.replace(/_/g, ' ')}
                                </span>
                                {content}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Show raw data if no parsed evaluation */}
                    {!parsedEvaluation && evaluation.evaluation && (
                      <div className="mb-6">
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <FiFileText className="text-blue-400" />
                          Raw Evaluation Data
                        </h4>
                        <div className="bg-slate-800/60 rounded-lg p-4 max-h-64 overflow-y-auto">
                          <pre className="text-slate-300 text-xs whitespace-pre-wrap">
                            {evaluation.evaluation}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Admin Context Videos (Legacy) */}
        {profiles.length > 0 && (
          <div className="bg-slate-800/40 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FiVideo className="text-blue-400" />
              Additional Interview Videos ({profiles.length})
            </h2>
            
            <div className="space-y-6">
              {profiles.map((profile, index) => (
                <div key={profile.id || index} className="bg-slate-700/40 rounded-lg p-5 border border-slate-600">
                  {profile.candidateVideo && (
                    <div className="mb-4">
                      <div
                        className="w-full rounded-lg border border-slate-600 overflow-hidden bg-black"
                        style={{ aspectRatio: "16/9", maxHeight: "400px" }}
                      >
                        <ReactPlayer
                          url={profile.candidateVideo}
                          controls
                          width="100%"
                          height="100%"
                          config={getWebMPlayerConfig(profile.candidateVideo)}
                          onError={(e) => console.error("Legacy video playback error:", e, profile.candidateVideo)}
                        />
                      </div>
                    </div>
                  )}
                  
                  {profile.candidateContextTextFile && (
                    <ResumeTextDisplay candidateContextTextFile={profile.candidateContextTextFile} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Data Message */}
        {!loading && interviewEvaluations.length === 0 && profiles.length === 0 && (
          <div className="bg-slate-800/40 rounded-xl p-12 text-center">
            <FiUser className="text-slate-600 text-5xl mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Not Yet Screened</h3>
            <p className="text-slate-400">This candidate has not completed any interview evaluations yet.</p>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal />
    </div>
  );
};

// Component to display resume text from context file
const ResumeTextDisplay = ({ candidateContextTextFile }) => {
  const [transcript, setTranscript] = useState("");
  const [rating, setRating] = useState("");

  useEffect(() => {
    const fetchResumeText = async () => {
      try {
        const response = await fetch(candidateContextTextFile);
        const text = await response.text();

        // Extract transcript
        const transcriptStart = text.indexOf("Full Interview TranScript:");
        const transcriptEnd = text.indexOf("******************************************Candidate Reting:***");
        if (transcriptStart !== -1) {
          setTranscript(text.substring(transcriptStart, transcriptEnd !== -1 ? transcriptEnd : text.length));
        }

        // Extract rating
        const ratingStart = text.indexOf("******************************************Candidate Reting:***");
        if (ratingStart !== -1) {
          setRating(text.substring(ratingStart));
        }
      } catch (error) {
        console.error("Error fetching resume text:", error);
      }
    };

    fetchResumeText();
  }, [candidateContextTextFile]);

  return (
    <div className="space-y-4">
      {transcript && (
        <div>
          <h5 className="text-purple-400 font-medium mb-2">Transcript</h5>
          <pre className="bg-slate-800/60 rounded-lg p-4 text-slate-300 text-xs overflow-x-auto whitespace-pre-wrap">
            {transcript}
          </pre>
        </div>
      )}
      {rating && (
        <div>
          <h5 className="text-purple-400 font-medium mb-2">Evaluation</h5>
          <pre className="bg-slate-800/60 rounded-lg p-4 text-slate-300 text-xs overflow-x-auto whitespace-pre-wrap">
            {rating}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AdminCandidateViewProfile;
